import React, { useMemo, useState } from "react";
import { ImageCarousel } from "./ImageCarousel";
import { SearchResultsProps } from "@/types/searchResults";
import { Button } from "@/components/ui/button";
import { getFaviconFromUrl } from "@/utils/favicon";
import { ExternalLink, Globe, X } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

export const SearchSourcesButton: React.FC<{
    results: SearchResultsProps["results"];
    isDarkMode: boolean;
    className?: string;
    variant?: "default" | "compact";
    isImageMode?: boolean;
}> = ({ results, isDarkMode, className, variant = "default", isImageMode }) => {
    const [isSourcesOpen, setIsSourcesOpen] = useState(false);

    const convertedSources = useMemo(() => {
        if (!results.results || results.results.length === 0)
            return [] as Array<{ title: string; url: string; favicon?: string }>;
        return results.results.map((source) => ({
            title: (() => {
                try {
                    const u = new URL(source.url);
                    return u.hostname.replace(/^www\./, "");
                } catch {
                    return source.url;
                }
            })(),
            url: source.url,
            favicon: getFaviconFromUrl(source.url),
        }));
    }, [results.results]);

    const openSources = () => {
        const isDesktop =
            typeof window !== "undefined" &&
            window.matchMedia("(min-width: 640px)").matches;

        if (isDesktop) {
            setIsSourcesOpen(true);
            return;
        }

        const event = new CustomEvent("openSourcesDrawer", {
            detail: {
                sources: convertedSources,
                searchDepth: results.searchDepth || "basic",
                responseTime: results.response_time || 0,
                isDarkMode,
            },
        });
        window.dispatchEvent(event);
    };

    if (!results.results || results.results.length === 0) return null;

    const isCompact = variant === "compact";

    return (
        <>
            <Button
                className={`sources-button w-[max-content] justify-center relative rounded-full font-medium cursor-pointer overflow-hidden bg-transparent flex items-center gap-1.5 ${isCompact
                    ? "h-6.5 px-2 text-xs"
                    : "pr-3 pl-1 py-2.5 text-sm"
                    } ${isImageMode
                        ? isDarkMode
                            ? "bg-white/5 hover:bg-white/10 text-white backdrop-blur-sm shadow-sm"
                            : "bg-white/40 hover:bg-white/60 text-black  backdrop-blur-sm shadow-sm"
                        : isDarkMode
                            ? "hover:bg-[#2a2a2a] text-white"
                            : "hover:bg-[#f2f2f2] text-[#2e2e2e]"
                    } ${className || ""}`}
                onClick={openSources}
            >
                <div className="flex -space-x-2">
                    {results.results.slice(0, 3).map((source, index) => {
                        const favicon = getFaviconFromUrl(source.url);
                        return favicon ? (
                            <img
                                key={index}
                                src={favicon}
                                alt="favicon"
                                className={`${isCompact ? "w-5 h-5" : "w-6 h-6"} ${isDarkMode ? "border-white/60" : "border-[#2a2a2a50]"} border rounded-full object-contain`}
                                style={{ zIndex: 10 - index }}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = "none";
                                }}
                            />
                        ) : null;
                    })}
                    {results.results.length > 3 && !isCompact && (
                        <div
                            className="w-6 h-6 pl-1 rounded-full bg-gray-500 text-white text-xs flex items-center justify-center border border-white/60 dark:border-[#2a2a2a]"
                            style={{ zIndex: 6 }}
                        >
                            {results.results.length}
                        </div>
                    )}
                </div>
                {!isCompact && <span>View Sources</span>}
                {isCompact && (
                    <span className="leading-none">Sources</span>
                )}
            </Button>

            <Dialog open={isSourcesOpen} onOpenChange={setIsSourcesOpen}>
                <DialogContent
                    className={`max-w-2xl p-0 overflow-hidden gap-0 rounded-2xl ${isDarkMode
                        ? "bg-[#1b1b1b] text-white border-white/10"
                        : "bg-white text-[#0f0f0f] border-black/10"
                        }`}
                >
                    <div className="px-5 pt-4 pb-3">
                        <DialogHeader className="space-y-0">
                            <div className="flex items-center justify-between mb-2">
                                <DialogTitle className={`text-base font-semibold ${isDarkMode ? "text-white" : "text-black"}`}>
                                    SOURCES
                                </DialogTitle>
                            </div>
                            <div className={`mt-3 h-px ${isDarkMode ? "bg-white/10" : "bg-black/10"}`} />
                        </DialogHeader>
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto px-5 pb-5">
                        <div className="space-y-4">
                            {convertedSources.slice(0, 3).map((source, index) => (
                                <a
                                    key={`${source.url}-${index}`}
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`block rounded-2xl bg-transparent px-4 py-3 transition ${isDarkMode
                                        ? "hover:bg-white/10"
                                        : "hover:bg-black/10"
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`h-9 w-9 shrink-0 rounded-xl border flex items-center justify-center overflow-hidden ${isDarkMode
                                                ? "border-white/10 bg-black/20"
                                                : "border-black/10 bg-white"
                                                }`}
                                        >
                                            {source.favicon ? (
                                                <img
                                                    src={source.favicon}
                                                    alt=""
                                                    className="h-5 w-5 object-contain"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = "none";
                                                    }}
                                                />
                                            ) : (
                                                <Globe className={`h-4 w-4 ${isDarkMode ? "text-white/60" : "text-black/50"}`} />
                                            )}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className={`text-xs ${isDarkMode ? "text-white/70" : "text-black/60"}`}>
                                                {source.title}
                                            </div>
                                        </div>

                                    </div>
                                </a>
                            ))}

                            {convertedSources.slice(3).map((source, index) => (
                                <a
                                    key={`${source.url}-more-${index}`}
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`block rounded-2xl bg-transparent px-4 py-3 transition ${isDarkMode
                                        ? "hover:bg-white/10"
                                        : "hover:bg-black/10"
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`h-9 w-9 shrink-0 rounded-xl border flex items-center justify-center overflow-hidden ${isDarkMode
                                                ? "border-white/10 bg-black/20"
                                                : "border-black/10 bg-white"
                                                }`}
                                        >
                                            {source.favicon ? (
                                                <img
                                                    src={source.favicon}
                                                    alt=""
                                                    className="h-5 w-5 object-contain"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = "none";
                                                    }}
                                                />
                                            ) : (
                                                <Globe className={`h-4 w-4 ${isDarkMode ? "text-white/60" : "text-black/50"}`} />
                                            )}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className={`text-xs ${isDarkMode ? "text-white/70" : "text-black/60"}`}>
                                                {source.title}
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export const SearchResults: React.FC<SearchResultsProps & { isImageMode?: boolean; renderMode?: 'sources' | 'full' }> = ({
    results,
    isDarkMode,
    hideSourcesButton,
    isImageMode,
    renderMode = 'sources',
}) => {
    const hasAny = (Array.isArray(results.results) && results.results.length > 0) || (Array.isArray(results.images) && results.images.length > 0);
    if (!hasAny) return null;

    const showFull = renderMode === 'full';

    const getSiteName = (url: string) => {
        if (!url) return '';
        try {
            const u = new URL(url);
            const host = u.hostname.replace(/^www\./i, '');
            const parts = host.split('.').filter(Boolean);
            if (parts.length <= 1) return host;
            const tld = parts[parts.length - 1];
            const sld = parts[parts.length - 2];
            const commonSld = new Set(['co', 'com', 'org', 'net', 'gov', 'edu']);
            if (commonSld.has(sld) && parts.length >= 3) return parts[parts.length - 3];
            if (tld && tld.length <= 3) return sld;
            return parts[0];
        } catch {
            const safe = url.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
            return safe.split('/')[0].split('.')[0] || safe;
        }
    };

    const sourcesList = React.useMemo(() => {
        const urls = (Array.isArray(results.results) ? results.results : [])
            .map((r) => r?.url)
            .filter((u): u is string => typeof u === 'string' && !!u);
        const seen = new Set<string>();
        return urls
            .filter((u) => {
                const key = getSiteName(u).toLowerCase();
                if (!key) return false;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map((u) => ({ url: u, name: getSiteName(u) }));
    }, [results.results]);

    return (
        <div className="w-full relative">

            {showFull && results.answer && (
                <div className={`rounded-2xl border p-4 ${isDarkMode ? 'border-white/10 bg-white/5 text-white' : 'border-black/10 bg-black/5 text-black'}`}>
                    <div className={`text-xs font-semibold tracking-widest uppercase opacity-70 ${isDarkMode ? 'text-white/70' : 'text-black/60'}`}>
                        Answer
                    </div>
                    <div className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">
                        {results.answer}
                    </div>
                </div>
            )}

            {/* Images Carousel (thumbnail view) */}
            {results.images && results.images.length > 0 && (
                <ImageCarousel images={results.images} isDarkMode={isDarkMode} />
            )}

            {showFull && sourcesList.length > 0 && (
                <div className="mt-4">
                    <div className={`text-xs font-semibold tracking-widest uppercase opacity-70 ${isDarkMode ? 'text-white/70' : 'text-black/60'}`}>
                        Sources
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {sourcesList.map((s, idx) => (
                            <a
                                key={`${s.url}-${idx}`}
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${isDarkMode
                                    ? 'border-white/10 bg-white/5 text-white hover:bg-white/10'
                                    : 'border-black/10 bg-black/5 text-black hover:bg-black/10'
                                    }`}
                            >
                                <span className="max-w-[160px] truncate">{s.name}</span>
                                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {results.results && results.results.length > 0 && !hideSourcesButton && !showFull && (
                <>
                    <div className="mt-4 block">
                        <SearchSourcesButton results={results} isDarkMode={isDarkMode} className="mt-2" isImageMode={isImageMode} />
                    </div>
                </>
            )}
        </div>
    );
};
